import React, {useState, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Tag from './Tag'
import Modal from '../shared/Modal'
import RuleForm from './RuleForm'
import Pagination from '../pagination'
import {
  fetchServiceIdRules,
  createServiceIdRule,
  updateServiceIdRuleThunk,
  deleteServiceIdRuleThunk,
} from '../../app-store/productRules/productRulesThunk'
import {
  COUNTRY_LANG,
  REGIONS,
  USER_ROLES,
  CALL_TYPES,
} from '../../utils/constants'
import {useAuthClient} from '../../utils/use-auth-client'

const ALL = '__ALL__'

export default function ManageServiceIds() {
  const dispatch = useDispatch()
  const client = useAuthClient()

  const {
    serviceIdRules: rules = [],
    productGroups: rawProductGroups = [],
    loading,
    error,
  } = useSelector(s => s.productRules)

  const productGroupOptions = useMemo(
    () =>
      [...new Set(rawProductGroups.map(r => r.product_group))].sort((a, b) =>
        a.localeCompare(b, undefined, {sensitivity: 'base'}),
      ),
    [rawProductGroups],
  )

  const [country, setCountry] = useState(ALL)
  const [language, setLanguage] = useState(ALL)
  const [region, setRegion] = useState(ALL)
  const [userRole, setUserRole] = useState(ALL)
  const [callType, setCallType] = useState(Object.keys(CALL_TYPES)[0])
  const [products, setProducts] = useState([])
  const [serviceIdQuery, setServiceIdQuery] = useState('')
  const [revealed, setRevealed] = useState(false)

  const [sort, setSort] = useState({key: 'country', dir: 'asc'})
  const [modal, setModal] = useState(null)
  const [page, setPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState(10)

  const isAllCountries = country === ALL
  // Language/Region dropdowns are active only when a specific non-US or US country is selected
  const isUS = country === 'US'
  const canSelectLangRegion = !isAllCountries && isUS

  const langs = isAllCountries
    ? ['All']
    : COUNTRY_LANG[country]?.languages || ['English']

  const handleCountryChange = c => {
    setCountry(c)
    if (c === ALL) {
      setLanguage(ALL)
      setRegion(ALL)
    } else {
      setLanguage((COUNTRY_LANG[c]?.languages || ['English'])[0])
      setRegion('')
    }
    setRevealed(false)
  }

  const handleFind = () => {
    dispatch(
      fetchServiceIdRules({
        client,
        filters: {
          ...(country !== ALL && {country}),
          ...(language !== ALL && {language}),
          ...(effectiveRegion !== ALL && {region: effectiveRegion}),
          ...(userRole !== ALL && {user_role: userRole}), // ← conditional
          ...(callType !== ALL && {appointment_type: callType}), // ← conditional
          ...(products.length > 0 && {productGroups: products}),
          ...(serviceIdQuery.trim() && {serviceId: serviceIdQuery.trim()}),
        },
      }),
    )
    setRevealed(true)
    setPage(1)
  }

  const effectiveRegion = region === ALL ? ALL : region || 'Default'

  const matches = useMemo(() => {
    if (!rules.length) return []
    const q = serviceIdQuery.trim().toLowerCase()
    const core = rules.filter(
      r =>
        (country === ALL || r.country === country) &&
        (language === ALL || r.language === language) &&
        (region === ALL || r.region === effectiveRegion) &&
        (userRole === ALL || r.userRole === userRole) && // ← was always comparing
        (callType === ALL || r.callType === callType) && // ← add if callType gets ALL too
        (!q || r.serviceId?.toLowerCase().includes(q)),
    )
    if (products.length === 0) return core
    const withProducts = core.filter(
      r =>
        r.productGrp.length > 0 && products.some(p => r.productGrp.includes(p)),
    )
    return withProducts.length > 0 ? withProducts : core
  }, [
    rules,
    serviceIdQuery,
    country,
    language,
    effectiveRegion,
    region,
    userRole,
    callType,
    products,
  ])

  const match = matches[0] || null

  const saveRule = (form, isEdit) => {
    if (isEdit) {
      dispatch(updateServiceIdRuleThunk({client, data: form}))
    } else {
      dispatch(createServiceIdRule({client, payload: form}))
    }
    setModal(null)
  }

  const deleteRule = rule => {
    dispatch(
      deleteServiceIdRuleThunk({
        client,
        payload: {
          language: rule.language,
          country: rule.country,
          region: rule.region,
          userRole: rule.userRole,
          callType: rule.callType,
          productGrp: rule.productGrp,
          serviceId: rule.serviceId,
        },
      }),
    )
    setModal(null)
  }

  const handleSort = k => {
    setSort(s => ({
      key: k,
      dir: s.key === k && s.dir === 'asc' ? 'desc' : 'asc',
    }))
    setPage(1)
  }
  const si = k => (sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '')

  const sortedMatches = useMemo(
    () =>
      [...matches].sort((a, b) => {
        const va = Array.isArray(a[sort.key])
          ? a[sort.key].join(',')
          : a[sort.key] || ''
        const vb = Array.isArray(b[sort.key])
          ? b[sort.key].join(',')
          : b[sort.key] || ''
        return sort.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
      }),
    [matches, sort],
  )

  const totalPages = Math.max(
    1,
    Math.ceil(sortedMatches.length / recordsPerPage),
  )
  const pagedMatches = sortedMatches.slice(
    (page - 1) * recordsPerPage,
    page * recordsPerPage,
  )

  const filterSummary = [
    country === ALL ? 'All countries' : country,
    language === ALL ? 'All languages' : language,
    isUS && region !== ALL ? effectiveRegion : null,
    userRole !== ALL ? userRole : null,
    serviceIdQuery.trim() ? `service ID "${serviceIdQuery.trim()}"` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const disabledSelectStyle = {
    width: '100%',
    padding: '7px 10px',
    fontSize: 13,
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#F3F4F6',
    color: '#9CA3AF',
    cursor: 'not-allowed',
    boxSizing: 'border-box',
  }

  const activeSelectStyle = {
    width: '100%',
    padding: '7px 10px',
    fontSize: 13,
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
  }

  return (
    <div style={{padding: '1.5rem'}}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
        }}
      >
        <p style={{margin: 0, fontSize: 13, color: '#9CA3AF'}}>
          Look up a service ID or manage existing rules
        </p>
        <button
          onClick={() => setModal({type: 'create'})}
          style={{
            padding: '8px 16px',
            background: '#ff5000',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          + New rule
        </button>
      </div>

      {/* Lookup panel */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: '1.25rem',
          marginBottom: '1.5rem',
        }}
      >
        <p
          style={{
            margin: '0 0 1rem',
            fontSize: 13,
            fontWeight: 600,
            color: '#111',
          }}
        >
          Look up service ID
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px 14px',
          }}
        >
          {/* Country */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            Country
            <select
              value={country}
              onChange={e => handleCountryChange(e.target.value)}
              style={activeSelectStyle}
            >
              <option value={ALL}>All</option>
              {Object.entries(COUNTRY_LANG).map(([key, {name}]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          {/* Language */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            Language
            <select
              value={language}
              disabled={isAllCountries || !isUS}
              onChange={e => {
                setLanguage(e.target.value)
                setRevealed(false)
              }}
              style={
                isAllCountries || !isUS
                  ? disabledSelectStyle
                  : activeSelectStyle
              }
            >
              {isAllCountries ? (
                <option value={ALL}>All</option>
              ) : (
                <>
                  <option value={ALL}>All</option>
                  {langs.map(l => (
                    <option key={l}>{l}</option>
                  ))}
                </>
              )}
            </select>
          </label>

          {/* Region */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            Region
            <select
              value={region}
              disabled={!canSelectLangRegion}
              onChange={e => {
                setRegion(e.target.value)
                setRevealed(false)
              }}
              style={
                canSelectLangRegion ? activeSelectStyle : disabledSelectStyle
              }
            >
              {isAllCountries ? (
                <option value={ALL}>All</option>
              ) : (
                <>
                  <option value={ALL}>All</option>
                  <option value="">Default</option>
                  {isUS
                    ? REGIONS.US.map(r => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))
                    : null}
                </>
              )}
            </select>
          </label>

          {/* User role */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            User role
            <select
              value={userRole}
              onChange={e => {
                setUserRole(e.target.value)
                setRevealed(false)
              }}
              style={activeSelectStyle}
            >
              <option value={ALL}>All</option>
              {USER_ROLES.map(r => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>

          {/* Appointment type */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
              gridColumn: '2 / -1',
            }}
          >
            Appointment type
            <select
              value={callType}
              onChange={e => {
                setCallType(e.target.value)
                setRevealed(false)
              }}
              style={activeSelectStyle}
            >
              {Object.entries(CALL_TYPES).map(([apiKey, label]) => (
                <option key={apiKey} value={apiKey}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          {/* Service ID */}
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            Service ID
            <span style={{fontWeight: 400, color: '#9CA3AF', fontSize: 11}}>
              (optional)
            </span>
            <input
              type="text"
              value={serviceIdQuery}
              onChange={e => {
                setServiceIdQuery(e.target.value)
                setRevealed(false)
              }}
              onKeyDown={e => e.key === 'Enter' && handleFind()}
              placeholder="e.g. SVC-1234"
              style={{
                ...activeSelectStyle,
              }}
            />
          </label>
        </div>

        <div style={{marginTop: 10}}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            <span>
              Product groups{' '}
              <span style={{fontWeight: 400, color: '#9CA3AF'}}>
                (optional — narrows results)
              </span>
            </span>
            <div
              style={{
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                background: '#fff',
                maxHeight: 300,
                overflowY: 'auto',
              }}
            >
              {productGroupOptions.map(p => (
                <label
                  key={p}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 10px',
                    fontSize: 13,
                    color: '#374151',
                    borderBottom: '0.5px solid #F3F4F6',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={products.includes(p)}
                    onChange={e => {
                      setProducts(v =>
                        e.target.checked ? [...v, p] : v.filter(x => x !== p),
                      )
                      setRevealed(false)
                    }}
                    style={{accentColor: '#ff5000'}}
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>
          {products.length > 0 && (
            <div
              style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5}}
            >
              {products.map(p => (
                <Tag
                  key={p}
                  color="blue"
                  onRemove={() => {
                    setProducts(v => v.filter(x => x !== p))
                    setRevealed(false)
                  }}
                >
                  {p}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleFind}
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: '#ff5000',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Searching…' : 'Find service ID'}
          </button>

          {revealed && error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                padding: '10px 16px',
              }}
            >
              <span style={{fontSize: 13, color: '#991B1B'}}>
                {typeof error === 'string'
                  ? error
                  : 'No rule found for this combination.'}
              </span>
            </div>
          )}

          {revealed && !loading && !error && !match && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                padding: '10px 16px',
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 13, color: '#991B1B'}}>
                No rule found for this combination.
              </span>
              {/* Only offer "Create rule" when a specific country is selected */}
              {!isAllCountries && (
                <button
                  onClick={() =>
                    setModal({
                      type: 'create',
                      prefill: {
                        language: language === ALL ? '' : language,
                        country,
                        region: region === ALL ? 'Default' : effectiveRegion,
                        userRole,
                        callType,
                        productGrp: products,
                        serviceId: '',
                      },
                    })
                  }
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#fff',
                    background: '#ff5000',
                    border: 'none',
                    borderRadius: 4,
                    padding: '4px 12px',
                    cursor: 'pointer',
                  }}
                >
                  + Create rule
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {revealed && !loading && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{fontSize: 13, fontWeight: 600, color: '#111'}}>
              Matching rules
              <span style={{fontWeight: 400, color: '#9CA3AF', marginLeft: 6}}>
                for {filterSummary}
              </span>
            </span>
            <span style={{fontSize: 12, color: '#9CA3AF'}}>
              {sortedMatches.length} found
            </span>
          </div>
          <div style={{overflowX: 'auto'}}>
            <table
              style={{width: '100%', borderCollapse: 'collapse', minWidth: 700}}
            >
              <thead>
                <tr>
                  {[
                    ['Country', 'country'],
                    ['Language', 'language'],
                    ['Region', 'region'],
                    ['Role', 'userRole'],
                    ['Appointment type', 'appointmentType'],
                    ['Product groups', 'productGrp'],
                    ['Service ID', 'serviceId'],
                    ['Actions', null],
                  ].map(([l, k]) => (
                    <th
                      key={l}
                      onClick={k ? () => handleSort(k) : undefined}
                      style={{
                        padding: '9px 12px',
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#6B7280',
                        borderBottom: '1px solid #E5E7EB',
                        background: '#F9FAFB',
                        whiteSpace: 'nowrap',
                        cursor: k ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      {l}
                      {si(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedMatches.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: '2rem',
                        fontSize: 13,
                        color: '#9CA3AF',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                      }}
                    >
                      No rules match the selected filters.
                    </td>
                  </tr>
                )}
                {pagedMatches.map(rule => (
                  <tr key={rule.id}>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      <strong style={{fontWeight: 500}}>{rule.country}</strong>
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {rule.language}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {rule.region}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {rule.userRole}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 11,
                        color: '#6B7280',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {CALL_TYPES[rule.appointmentType] ?? rule.callType}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {rule.productGrp.length === 0 ? (
                        <Tag color="'#ff5000'">Default (fallback)</Tag>
                      ) : (
                        <div
                          style={{display: 'flex', flexWrap: 'wrap', gap: 3}}
                        >
                          {rule.productGrp.map(p => (
                            <Tag key={p} color="blue">
                              {p}
                            </Tag>
                          ))}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      {rule.serviceId}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: '#374151',
                        borderBottom: '1px solid #F3F4F6',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div style={{display: 'flex', gap: 6}}>
                        <button
                          onClick={() => setModal({type: 'edit', rule})}
                          style={{
                            fontSize: 11,
                            padding: '3px 9px',
                            border: '1px solid #D1D5DB',
                            borderRadius: 4,
                            background: '#fff',
                            cursor: 'pointer',
                            color: '#374151',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setModal({type: 'delete', rule})}
                          style={{
                            fontSize: 11,
                            padding: '3px 9px',
                            border: '1px solid #FECACA',
                            borderRadius: 4,
                            background: '#fff',
                            cursor: 'pointer',
                            color: '#DC2626',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sortedMatches.length > 0 && (
            <Pagination
              totalRecords={sortedMatches.length}
              recordsPerPage={recordsPerPage}
              setCurrentPage={setPage}
              currentPage={page}
              setRecordsPerPage={val => {
                setRecordsPerPage(val)
                setPage(1)
              }}
              totalPages={totalPages}
            />
          )}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'create' && (
        <Modal title="Create new rule" onClose={() => setModal(null)}>
          <RuleForm
            init={modal.prefill || null}
            allRules={rules}
            productGroupOptions={productGroupOptions}
            onSave={form => saveRule(form, false)}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {modal?.type === 'edit' && (
        <Modal title="Edit rule" onClose={() => setModal(null)}>
          <RuleForm
            init={modal.rule}
            allRules={rules}
            productGroupOptions={productGroupOptions}
            onSave={form => saveRule(form, true)}
            onClose={() => setModal(null)}
            type="edit"
          />
        </Modal>
      )}
      {modal?.type === 'delete' && (
        <Modal title="Delete rule?" onClose={() => setModal(null)}>
          <p
            style={{
              fontSize: 13,
              color: '#6B7280',
              margin: '0 0 1rem',
              lineHeight: 1.6,
            }}
          >
            This will permanently remove the rule for{' '}
            <strong style={{color: '#111'}}>
              {modal.rule.country}
              {modal.rule.region ? ` / ${modal.rule.region}` : ''} /{' '}
              {modal.rule.language}
            </strong>{' '}
            with service ID{' '}
            <code
              style={{
                fontFamily: 'monospace',
                fontSize: 12,
                background: '#F3F4F6',
                padding: '1px 5px',
                borderRadius: 4,
              }}
            >
              {modal.rule.serviceId}
            </code>
            .
          </p>
          <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
            <button
              onClick={() => setModal(null)}
              style={{
                fontSize: 13,
                padding: '7px 15px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
                color: '#374151',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => deleteRule(modal.rule)}
              style={{
                fontSize: 13,
                padding: '7px 15px',
                borderRadius: 6,
                border: 'none',
                background: '#DC2626',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
